import { useMemo } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { useOnlineUserIds } from '../../hooks/usePresence';
import type { ChatMemberInfo } from '../../lib/teamChatApi';
import { Avatar } from '../tasks/linear/Avatar';
import { AvatarWithPresence } from './PresenceDot';

function MemberRow({
  member,
  online,
  onClick,
}: {
  member: ChatMemberInfo;
  online: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left hover:bg-accent ${
        online ? '' : 'opacity-40 hover:opacity-100'
      }`}
    >
      <AvatarWithPresence online={online} ringClassName="ring-background">
        <Avatar name={member.name || null} size={28} />
      </AvatarWithPresence>
      <span className="min-w-0 truncate text-sm text-foreground">{member.name}</span>
    </button>
  );
}

/**
 * Discord's right-hand roster for group chats: ONLINE first, then OFFLINE,
 * each alphabetical. Hidden below `lg` — the screen is too narrow there, and
 * the same list is reachable from the group settings modal.
 *
 * A click is reported upward and nothing more: what happens next (the DM with
 * that person opens, their profile takes this column) is decided by the
 * conversation view, because it outlives this roster — the view is keyed by
 * conversation and the click navigates to a different one.
 */
export function MemberListPanel({
  members,
  onMemberClick,
}: {
  members: ChatMemberInfo[];
  onMemberClick: (userId: string) => void;
}) {
  const { t } = useI18n();
  const onlineIds = useOnlineUserIds();

  const { online, offline } = useMemo(() => {
    const byName = [...members].sort((a, b) => a.name.localeCompare(b.name));
    return {
      online: byName.filter((m) => onlineIds.has(m.id)),
      offline: byName.filter((m) => !onlineIds.has(m.id)),
    };
  }, [members, onlineIds]);

  return (
    <aside className="hidden w-60 shrink-0 flex-col overflow-y-auto border-l border-border px-2 py-4 lg:flex">
      {online.length > 0 && (
        <section>
          <h2 className="px-2 pb-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">
            {t('teamChat.online')} — {online.length}
          </h2>
          {online.map((m) => (
            <MemberRow key={m.id} member={m} online onClick={() => onMemberClick(m.id)} />
          ))}
        </section>
      )}
      {offline.length > 0 && (
        <section className={online.length > 0 ? 'mt-4' : ''}>
          <h2 className="px-2 pb-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">
            {t('teamChat.offline')} — {offline.length}
          </h2>
          {offline.map((m) => (
            <MemberRow key={m.id} member={m} online={false} onClick={() => onMemberClick(m.id)} />
          ))}
        </section>
      )}
    </aside>
  );
}
